import { Decoration, DecorationSet, EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';
import katex from 'katex';
import 'katex/dist/katex.min.css';

export const mathClickablePlugin = ViewPlugin.fromClass(class {
  decorations: DecorationSet;
  private modal: HTMLElement | null = null;
  private currentFormulaRange: { start: number; end: number } | null = null;
  private closeModalListener: ((e: MouseEvent) => void) | null = null;
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  private isDragging: boolean = false;
  private dragOffsetX: number = 0;
  private dragOffsetY: number = 0;
  private dragListener: ((e: MouseEvent) => void) | null = null;
  private dragEndListener: ((e: MouseEvent) => void) | null = null;
  private userMoved: boolean = false;

  constructor(view: EditorView) {
    this.decorations = this.createDecorations(view);
  }

  update(update: ViewUpdate) {
    if (update.docChanged && this.modal && this.currentFormulaRange) {
      // Recalculate the formula range based on the current document state
      const docText = update.state.doc.toString();
      // Updated regex to better handle nested environments
      const mathRegex = /(?:\$[^$]+\$|\\\([^)]+\\\)|\\\[[^\]]+\\\]|\\begin\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\}[\s\S]*?\\end\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\})/g;
      
      // Find the formula at or near the current range
      let match;
      let foundFormula: string | null = null;
      let newStart = 0;
      let newEnd = 0;
      
      // Look for formulas around the current position
      const searchStart = Math.max(0, this.currentFormulaRange.start - 20);
      const searchEnd = Math.min(docText.length, this.currentFormulaRange.end + 20);
      const searchText = docText.substring(searchStart, searchEnd);
      
      mathRegex.lastIndex = 0;
      while ((match = mathRegex.exec(searchText)) !== null) {
        newStart = searchStart + match.index;
        newEnd = newStart + match[0].length;
        
        // If this formula overlaps with our current range, it's likely the updated version
        if (newEnd >= this.currentFormulaRange.start && newStart <= this.currentFormulaRange.end) {
          foundFormula = match[0];
          this.currentFormulaRange = { start: newStart, end: newEnd };
          break;
        }
      }
      
      if (foundFormula) {
        this.renderFormula(this.modal, foundFormula);
      } else {
        // If we can't find the formula anymore, close the preview
        this.cleanup();
      }
    }
    
    // Always update decorations on any document change to detect new formulas
    if (update.docChanged) {
      this.decorations = this.createDecorations(update.view);
    }
  }

  destroy() {
    this.cleanup();
  }

  private cleanup() {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
    this.userMoved = false;
    if (this.closeModalListener) {
      document.removeEventListener('click', this.closeModalListener);
      this.closeModalListener = null;
    }
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }
    if (this.dragListener) {
      document.removeEventListener('mousemove', this.dragListener);
      this.dragListener = null;
    }
    if (this.dragEndListener) {
      document.removeEventListener('mouseup', this.dragEndListener);
      this.dragEndListener = null;
    }
    this.currentFormulaRange = null;
  }

  isMathFormula(text: string): boolean {
    const mathPatterns = [
      /\$[^$]+\$/,                    // Inline math $...$
      /\\\([^)]+\\\)/,                // Inline math \(...\)
      /\\\[[^\]]+\\\]/,               // Display math \[...\]
      /\\begin\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\}[\s\S]*?\\end\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\}/ // Environment math
    ];
    
    return mathPatterns.some(pattern => pattern.test(text));
  }

  private renderFormula(container: HTMLElement, formula: string) {
    try {
      let cleanFormula = formula;
      
      // Check for equation with aligned environment inside
      if (formula.includes('\\begin{equation}') && formula.includes('\\begin{aligned}')) {
        // Keep the aligned environment but remove equation tags and labels
        cleanFormula = formula
          .replace(/\\begin\{equation\}|\\end\{equation\}/g, '')
          .replace(/\\label\{.*?\}/g, '')
          .trim();
      }
      // Special handling for different environments
      else if (formula.match(/\\begin\{(?:[pvVbB]matrix)\}/)) {
        // For matrix environments, keep the environment tags
        cleanFormula = formula.replace(/\\label\{.*?\}/g, '').trim();
      } 
      else if (formula.includes('\\begin{align') || formula.includes('\\begin{aligned}')) {
        // For align environments, keep the environment tags
        cleanFormula = formula.replace(/\\label\{.*?\}/g, '').trim();
      }
      else {
        // For other environments, proceed with normal cleaning
        cleanFormula = formula
          .replace(/^\$|\$/g, '')  // Remove $ delimiters
          .replace(/^\\\(|\\\)$/g, '')  // Remove \( \) delimiters
          .replace(/^\\\[|\\\]$/g, '')  // Remove \[ \] delimiters
          .replace(/\\begin\{equation\}|\\end\{equation\}/g, '')  // Remove equation environment
          .replace(/\\begin\{multline\}|\\end\{multline\}/g, '')  // Remove multline environment
          .replace(/\\begin\{multline\*\}|\\end\{multline\*\}/g, '')  // Remove multline* environment  
          .replace(/\\label\{.*?\}/g, '')  // Remove labels
          .trim();
      }

      cleanFormula = cleanFormula
        .replace(/\\iiiint/g, '\\int\\int\\int\\int')
        .replace(/\\idotsint/g, '\\int\\dots\\int');

      container.innerHTML = '';
      // Modal header with drag handle and close button
      const header = document.createElement('div');
      header.style.cssText = `
        width: 100%;
        min-width: 120px;
        max-width: 400px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #f5f5f5;
        border-bottom: 1px solid #eee;
        padding: 0 0 0 8px;
        font-size: 13px;
        color: #888;
        user-select: none;
        cursor: move;
        height: 32px;
        box-sizing: border-box;
        position: relative;
        z-index: 2;
      `;
      header.textContent = '';
      // Dragable title
      const dragTitle = document.createElement('span');
      dragTitle.textContent = 'Dragable';
      dragTitle.style.flex = '1';
      dragTitle.style.fontWeight = 'bold';
      // Close button
      const closeButton = document.createElement('button');
      closeButton.innerHTML = '&times;';
      closeButton.style.cssText = `
        margin-left: 8px;
        background: #f0f0f0;
        border: none;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        padding: 0 10px;
        line-height: 24px;
        color: #555;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        border-radius: 3px;
        z-index: 3;
        align-self: center;
      `;
      closeButton.title = "Close preview";
      closeButton.addEventListener('click', () => this.cleanup());
      header.appendChild(dragTitle);
      header.appendChild(closeButton);

      // Drag logic on header
      header.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.isDragging = true;
        this.userMoved = true;
        const rect = this.modal!.getBoundingClientRect();
        this.dragOffsetX = e.clientX - rect.left;
        this.dragOffsetY = e.clientY - rect.top;
        this.dragListener = (moveEvent: MouseEvent) => {
          if (!this.isDragging) return;
          let newLeft = moveEvent.clientX - this.dragOffsetX;
          let newTop = moveEvent.clientY - this.dragOffsetY;
          // Clamp to viewport
          const vw = window.innerWidth, vh = window.innerHeight;
          const mw = this.modal!.offsetWidth, mh = this.modal!.offsetHeight;
          newLeft = Math.max(0, Math.min(vw - mw, newLeft));
          newTop = Math.max(0, Math.min(vh - mh, newTop));
          this.modal!.style.left = `${newLeft}px`;
          this.modal!.style.top = `${newTop}px`;
        };
        this.dragEndListener = () => {
          this.isDragging = false;
          if (this.dragListener) {
            document.removeEventListener('mousemove', this.dragListener);
            this.dragListener = null;
          }
          if (this.dragEndListener) {
            document.removeEventListener('mouseup', this.dragEndListener);
            this.dragEndListener = null;
          }
        };
        document.addEventListener('mousemove', this.dragListener);
        document.addEventListener('mouseup', this.dragEndListener);
      });

      // Preview content
      const previewContent = document.createElement('div');
      previewContent.style.padding = '10px';
      previewContent.style.display = 'flex';
      previewContent.style.justifyContent = 'center';
      previewContent.style.alignItems = 'center';
      previewContent.style.minWidth = '120px';
      previewContent.style.maxWidth = '400px';
      previewContent.style.overflow = 'hidden';

      katex.render(cleanFormula, previewContent, {
        displayMode: true,
        throwOnError: false,
        strict: false,
        trust: true,
        macros: {
          '\\mathbf': '\\boldsymbol'
        }
      });

      container.appendChild(header);
      container.appendChild(previewContent);
    } catch (e) {
      console.log('Invalid math expression:', e);
      if (container) {
        container.textContent = 'Error rendering formula';
      }
    }
  }

  createDecorations(view: EditorView) {
  const widgets: any[] = [];
    
    // Create a click handler for the entire editor
    if (!view.dom.hasAttribute('data-math-click-handler')) {
      view.dom.setAttribute('data-math-click-handler', 'true');
      view.dom.addEventListener('click', (event) => {
        const pos = view.posAtCoords({x: event.clientX, y: event.clientY});
        if (pos === null) return;
        const docText = view.state.doc.toString();
        const mathRegex = /(?:\$[^$]+\$|\\\([^)]+\\\)|\\\[[^\]]+\\\]|\\begin\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\}[\s\S]*?\\end\{(?:equation|aligned|gather|multline|align|[pvVbB]matrix)\*?\})/g;
        let match;
        let foundFormula: string | null = null;
        let start = 0;
        let end = 0;
        mathRegex.lastIndex = 0;
        while ((match = mathRegex.exec(docText)) !== null) {
          start = match.index;
          end = start + match[0].length;
          if (pos >= start && pos <= end) {
            foundFormula = match[0];
            break;
          }
        }
        if (foundFormula) {
          this.cleanup();
          this.modal = document.createElement('div');
          this.modal.className = 'math-preview-modal';
          const coords = view.coordsAtPos(start);
          if (!coords) return;
          this.currentFormulaRange = { start, end };
          this.renderFormula(this.modal, foundFormula);
          if (this.modal) {
            this.modal.style.cssText = `
              position: absolute;
              top: -9999px;
              left: -9999px;
              font-size: 12px;
              background: white;
              padding: 8px;
              box-shadow: 0 0 10px rgba(0,0,0,0.3);
              z-index: 1000;
              max-width: 90vw;
              max-height: 90vh;
              overflow: hidden;
              border-radius: 6px;
              box-sizing: border-box;
            `;
          }
          document.body.appendChild(this.modal);
          const modalHeight = this.modal ? this.modal.offsetHeight : 0;
          const modalWidth = this.modal ? this.modal.offsetWidth : 0;
          const lineCoords = coords;
          if (!lineCoords) return;
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          let top, left;
          const spaceAbove = lineCoords.top;
          const spaceBelow = viewportHeight - lineCoords.bottom;
          if (!this.userMoved && this.modal) {
            if (modalHeight < spaceAbove) {
              const maxAbove = spaceAbove - 8;
              const actualHeight = Math.min(modalHeight, maxAbove - 8);
              top = Math.max(8, lineCoords.top - actualHeight - 8);
              this.modal.style.maxHeight = `${maxAbove}px`;
              this.modal.style.overflowY = modalHeight > maxAbove ? 'auto' : '';
            } else if (modalHeight < spaceBelow) {
              top = lineCoords.bottom + 8;
            } else {
              if (spaceBelow >= spaceAbove) {
                top = lineCoords.bottom + 8;
                this.modal.style.maxHeight = `${spaceBelow - 16}px`;
              } else {
                const maxAbove = spaceAbove - 8;
                top = 8;
                this.modal.style.maxHeight = `${maxAbove}px`;
              }
              this.modal.style.overflowY = 'auto';
            }
            if (lineCoords.left + modalWidth > viewportWidth) {
              left = Math.max(8, viewportWidth - modalWidth - 8);
            } else {
              left = lineCoords.left;
            }
            this.modal.style.top = `${top}px`;
            this.modal.style.left = `${left}px`;
          }
          this.closeModalListener = (e: MouseEvent) => {
            if (this.modal && !this.modal.contains(e.target as Node)) {
              this.cleanup();
            }
          };
          this.keydownListener = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.modal) {
              this.cleanup();
            }
          };
          setTimeout(() => {
            document.addEventListener('click', this.closeModalListener!);
            document.addEventListener('keydown', this.keydownListener!);
          }, 0);
        }
      });
    }

    return Decoration.set(widgets);
  }
});
